// Keep email/phone validation logic in sync with backend/src/shared/utils/validation.ts
import * as Yup from 'yup';
import i18n from '@/shared/i18n';

export const PHONE_ERROR_MSG = i18n.t('validation:phoneInvalid');

/** @deprecated Use isValidPhoneInput / isValidCanonicalPhone instead */
export const PHONE_REGEX = /^\+[1-9]\d{7,14}$/;

const PHONE_PUNCTUATION = /[\s.\-()]/g;

/** National significant number for a Vietnam subscriber (no trunk 0, no country code). */
function isValidVnNsn(nsn: string): boolean {
  if (/^[35789]/.test(nsn)) return nsn.length === 9;
  if (/^2/.test(nsn)) return nsn.length >= 9 && nsn.length <= 10;
  return false;
}

/**
 * Stored form is E.164 (`+` then 8–15 digits). `+84` also has to match a Vietnam
 * mobile or landline. Other countries only need the E.164 shape.
 */
export function isValidCanonicalPhone(normalized: string): boolean {
  if (!/^\+[1-9]\d{7,14}$/.test(normalized)) return false;
  if (normalized.startsWith('+84')) return isValidVnNsn(normalized.slice(3));
  return true;
}

/**
 * Accepts Vietnam national input (`0…`, `84…`, or the digits beside a +84 prefix)
 * and explicit international input (`+…` or `00…`). Returns E.164.
 */
export function parsePhoneInput(raw: string): string {
  const cleaned = raw.trim().replace(PHONE_PUNCTUATION, '');
  if (!cleaned) throw new Error(PHONE_ERROR_MSG);

  let digits: string;
  if (cleaned.startsWith('+')) {
    digits = cleaned.slice(1);
  } else if (cleaned.startsWith('00')) {
    digits = cleaned.slice(2);
  } else if (cleaned.startsWith('0')) {
    digits = `84${cleaned.slice(1)}`;
  } else if (/^84\d+$/.test(cleaned) && cleaned.length >= 11) {
    digits = cleaned;
  } else if (/^\d+$/.test(cleaned) && isValidVnNsn(cleaned)) {
    digits = `84${cleaned}`;
  } else {
    throw new Error(PHONE_ERROR_MSG);
  }

  if (!/^\d+$/.test(digits)) throw new Error(PHONE_ERROR_MSG);
  const e164 = `+${digits}`;
  if (!isValidCanonicalPhone(e164)) throw new Error(PHONE_ERROR_MSG);
  return e164;
}

/** Same result as `parsePhoneInput`. Throws when the input is not a phone number. */
export function normalizePhone(raw: string): string {
  return parsePhoneInput(raw);
}

export function parseOptionalPhoneInput(raw?: string): string | undefined {
  if (!raw?.trim()) return undefined;
  return parsePhoneInput(raw);
}

export function isValidPhoneInput(raw: string): boolean {
  try {
    parsePhoneInput(raw);
    return true;
  } catch {
    return false;
  }
}

export const EMAIL_ERROR_MSG = i18n.t('validation:emailInvalid');

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidEmail(email: string | undefined | null): boolean {
  if (typeof email !== 'string') return false;
  return EMAIL_PATTERN.test(email.trim());
}

export const phoneValidation = Yup.string().test(
  'vn-phone',
  PHONE_ERROR_MSG,
  (value) => !value || isValidPhoneInput(value)
);

export const requiredPhoneValidation = Yup.string()
  .required(i18n.t('validation:phoneRequired'))
  .test('vn-phone', PHONE_ERROR_MSG, (value) => !!value && isValidPhoneInput(value));

export const optionalEmailValidation = Yup.string().test(
  'email-format',
  EMAIL_ERROR_MSG,
  (value) => !value || isValidEmail(value)
);

export const emailValidation = optionalEmailValidation;

export const requiredEmailValidation = Yup.string()
  .required(i18n.t('validation:emailRequired'))
  .test('email-format', EMAIL_ERROR_MSG, (value) => !!value && isValidEmail(value));

export function formatPhone(phone: string | undefined | null): string {
  if (!phone) return 'Chưa cung cấp';
  const cleaned = phone.trim();
  if (cleaned.startsWith('+84')) {
    return `+84 ${cleaned.slice(3)}`;
  }
  if (/^84\d+$/.test(cleaned) && cleaned.length >= 11) {
    return `+84 ${cleaned.slice(2)}`;
  }
  return cleaned;
}

/**
 * Prefill `PhoneTextField` from a stored value.
 * VN numbers show the familiar national form with trunk `0`; others stay E.164.
 * Canonical E.164 is applied on save, not while typing.
 */
export function formatPhoneForInput(phone: string | undefined | null): string {
  if (!phone?.trim()) return '';
  try {
    const e164 = parsePhoneInput(phone);
    if (e164.startsWith('+84')) return `0${e164.slice(3)}`;
    return e164;
  } catch {
    return phone.trim();
  }
}

export function formatPhoneOrFallback(
  phone: string | undefined | null,
  fallback = '—'
): string {
  if (!phone?.trim()) return fallback;
  const formatted = formatPhone(phone);
  return formatted === 'Chưa cung cấp' ? fallback : formatted;
}

/** @deprecated Prefer `formatPatientInputLabel` / PatientSelect from `@/modules/patient`. */
export function formatPatientOptionLabel(
  name: string | null | undefined,
  phone?: string | null
): string {
  return `${name ?? ''} (${phone ? formatPhone(phone) : 'Không có SĐT'})`;
}

/** Keep in sync with backend passwordPolicyService defaults. */
export interface PasswordPolicyRules {
  passwordMinLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicyRules = {
  passwordMinLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
};

/** Keep in sync with backend `PASSWORD_MAX_LENGTH` (bcrypt 72-byte cap). */
export const PASSWORD_MAX_LENGTH = 72;
/** Keep in sync with backend `PASSWORD_MIN_LENGTH_FLOOR`. */
export const PASSWORD_MIN_LENGTH_FLOOR = 6;

const SPECIAL_CHARS = /[@$!%*?&]/;

export function passwordMeetsPolicy(
  password: string,
  policy: PasswordPolicyRules = DEFAULT_PASSWORD_POLICY
): boolean {
  if (typeof password !== 'string') return false;
  if (password.length < policy.passwordMinLength || password.length > PASSWORD_MAX_LENGTH) {
    return false;
  }
  if (policy.requireUppercase && !/[A-Z]/.test(password)) return false;
  if (policy.requireLowercase && !/[a-z]/.test(password)) return false;
  if (policy.requireNumbers && !/\d/.test(password)) return false;
  if (policy.requireSpecialChars && !SPECIAL_CHARS.test(password)) return false;
  return true;
}

export function passwordPolicyMessage(
  policy: PasswordPolicyRules = DEFAULT_PASSWORD_POLICY
): string {
  const requirements: string[] = [];
  if (policy.requireUppercase) requirements.push('uppercase');
  if (policy.requireLowercase) requirements.push('lowercase');
  if (policy.requireNumbers) requirements.push('number');
  if (policy.requireSpecialChars) requirements.push('special character (@$!%*?&)');
  let msg = `Password must be ${policy.passwordMinLength}–${PASSWORD_MAX_LENGTH} characters`;
  if (requirements.length) {
    msg += ` and include ${requirements.join(', ')}`;
  }
  return msg;
}

export type PasswordPolicyCheckId =
  | 'min'
  | 'uppercase'
  | 'lowercase'
  | 'number'
  | 'special';

/** Per-rule pass/fail for live password hints. Only includes rules the org policy enables. */
export function getPasswordPolicyChecks(
  password: string,
  policy: PasswordPolicyRules = DEFAULT_PASSWORD_POLICY
): { id: PasswordPolicyCheckId; met: boolean }[] {
  const value = typeof password === 'string' ? password : '';
  const checks: { id: PasswordPolicyCheckId; met: boolean }[] = [
    { id: 'min', met: value.length >= policy.passwordMinLength && value.length <= PASSWORD_MAX_LENGTH },
  ];
  if (policy.requireUppercase) {
    checks.push({ id: 'uppercase', met: /[A-Z]/.test(value) });
  }
  if (policy.requireLowercase) {
    checks.push({ id: 'lowercase', met: /[a-z]/.test(value) });
  }
  if (policy.requireNumbers) {
    checks.push({ id: 'number', met: /\d/.test(value) });
  }
  if (policy.requireSpecialChars) {
    checks.push({ id: 'special', met: SPECIAL_CHARS.test(value) });
  }
  return checks;
}

function secureRandomInt(max: number): number {
  if (max <= 0) return 0;
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] % max;
}

function pickRandomChar(charset: string): string {
  return charset[secureRandomInt(charset.length)] ?? charset[0] ?? '';
}

function shuffleChars(chars: string[]): string[] {
  const copy = [...chars];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = secureRandomInt(i + 1);
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Generate a random password that satisfies the given org policy (uses Web Crypto). */
export function generatePassword(
  policy: PasswordPolicyRules = DEFAULT_PASSWORD_POLICY
): string {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lowercase = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';

  const required: string[] = [];
  if (policy.requireUppercase) required.push(pickRandomChar(uppercase));
  if (policy.requireLowercase) required.push(pickRandomChar(lowercase));
  if (policy.requireNumbers) required.push(pickRandomChar(digits));
  if (policy.requireSpecialChars) required.push(pickRandomChar('@$!%*?&'));

  const pool =
    `${policy.requireUppercase ? uppercase : ''}`
    + `${policy.requireLowercase ? lowercase : ''}`
    + `${policy.requireNumbers ? digits : ''}`
    + `${policy.requireSpecialChars ? '@$!%*?&' : ''}`
    || `${uppercase}${lowercase}${digits}@$!%*?&`;

  const length = Math.min(
    PASSWORD_MAX_LENGTH,
    Math.max(policy.passwordMinLength, 14)
  );

  while (required.length < length) {
    required.push(pickRandomChar(pool));
  }

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const candidate = shuffleChars(required).join('');
    if (passwordMeetsPolicy(candidate, policy)) {
      return candidate;
    }
    required.push(pickRandomChar(pool));
  }

  return shuffleChars(required).join('').slice(0, length);
}

export function getPasswordPolicyViolation(
  password: string,
  policy: PasswordPolicyRules = DEFAULT_PASSWORD_POLICY
): 'min' | 'max' | 'uppercase' | 'lowercase' | 'number' | 'special' | 'complexity' | null {
  if (password.length < policy.passwordMinLength) return 'min';
  if (password.length > PASSWORD_MAX_LENGTH) return 'max';
  if (policy.requireUppercase && !/[A-Z]/.test(password)) return 'uppercase';
  if (policy.requireLowercase && !/[a-z]/.test(password)) return 'lowercase';
  if (policy.requireNumbers && !/\d/.test(password)) return 'number';
  if (policy.requireSpecialChars && !SPECIAL_CHARS.test(password)) return 'special';
  if (!passwordMeetsPolicy(password, policy)) return 'complexity';
  return null;
}

/** @deprecated Prefer passwordMeetsPolicy with org policy */
export const STRONG_PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

export function isStrongPassword(password: string): boolean {
  return passwordMeetsPolicy(password, DEFAULT_PASSWORD_POLICY);
}

export function buildPasswordValidation(policy: PasswordPolicyRules = DEFAULT_PASSWORD_POLICY) {
  return Yup.string()
    .required(i18n.t('validation:passwordRequired', { defaultValue: 'Password is required' }))
    .test('password-policy', passwordPolicyMessage(policy), (value) =>
      passwordMeetsPolicy(value || '', policy)
    );
}

export const strongPasswordValidation = buildPasswordValidation(DEFAULT_PASSWORD_POLICY);
